import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import convertToWGS84 from 'rd-to-wgs84';
import { from } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { read, utils } from 'xlsx';
import { CodaltComponent } from '../codalt.component';
import { Customer } from '../customer';
import { CustomerProject } from '../customer-project.interface';
import { LocalStorage } from '../local-storage.class';
import { ConfirmDialogService } from '../services/confirm-dialog-service/confirm-dialog.service';
import { CustomerProjectService } from '../services/customer-project/customer-project.service';
import { CustomerService } from '../services/customer/customer.service';
import { WorkareaService } from '../services/workarea/workarea.service';
import { FeaturePreviewDialogComponent } from '../shared/feature-preview-dialog/feature-preview-dialog.component';
import { Status } from '../status.interface';
import { Workarea } from '../workarea.class';
import { FieldOptionsService } from '../workarea/shared/field-options.service';
import { LocationService } from '../workarea/shared/location.service';

export interface ExcelRow {
    properties: Record<string, any>;
}

@Component({
    selector: 'app-import-excel',
    templateUrl: './import-excel.component.html',
    standalone: false,
})
export class ImportExcelComponent extends CodaltComponent implements OnInit {
    public readonly workareaFields: string[] = [
        'number',
        'street',
        'zipcode',
        'housenumber',
        'place',
        'road_number',
        'lat',
        'lng',
        'comments',
    ];

    public readonly workareaFieldMeta: Record<string, { label: string; description: string }> = {
        number: { label: 'Nummer', description: 'Het nummer van het werkvak.' },
        street: { label: 'Straat', description: '' },
        zipcode: { label: 'Postcode', description: '' },
        housenumber: { label: 'Huisnummer', description: '' },
        place: { label: 'Plaats', description: '' },
        road_number: { label: 'Wegnummer', description: 'Het wegnummer, bijv. N270.' },
        lat: { label: 'Breedtegraad', description: 'RD- of WGS84-breedtegraad.' },
        lng: { label: 'Lengtegraad', description: 'RD- of WGS84-lengtegraad.' },
        comments: { label: 'Opmerkingen', description: '' },
    };

    /** Loaded from FieldOptionsService (type 'general') */
    public extraFieldOptions: Record<string, string[]> = {};
    public extraFieldLabels: Record<string, string> = {};
    public fieldOptionsLoaded = false;

    /** Known Dutch translations for extra field keys */
    private readonly extraFieldLabelTranslations: Record<string, string> = {
        location_damage: 'Schade Locatie',
        roadblock: 'Afzetting',
        execution: 'Uitvoering',
        wearlayer: 'Slijtlaag',
        genpriority: 'Prioriteit',
        waymark: 'Wegmarkering',
        damage: 'Schade',
        toplayer: 'Toplaag',
        bottomlayer: 'Onderlaag',
        interlayer: 'Tussenlaag',
    };

    private readonly fieldNameMappings = new Map<string, string>([
        ['adres', 'street'],
        ['straat', 'street'],
        ['straatnaam', 'street'],
        ['postcode', 'zipcode'],
        ['huisnummer', 'housenumber'],
        ['nummer', 'number'],
        ['nr', 'number'],
        ['id', 'number'],
        ['identificatie', 'number'],
        ['melding', 'number'],
        ['plaats', 'place'],
        ['gemeente', 'place'],
        ['stad', 'place'],
        ['wegnummer', 'road_number'],
        ['weg_nr', 'road_number'],
        ['opmerking', 'comments'],
        ['opmerkingen', 'comments'],
        ['omschrijving', 'comments'],
        ['beschrijving', 'comments'],
        ['lat', 'lat'],
        ['latitude', 'lat'],
        ['breedtegraad', 'lat'],
        ['rd-breedtegraad', 'lat'],
        ['lng', 'lng'],
        ['lon', 'lng'],
        ['long', 'lng'],
        ['longitude', 'lng'],
        ['lengtegraad', 'lng'],
        ['rd-lengtegraad', 'lng'],
        ['locatie_schade', 'location_damage'],
        ['afzetting', 'roadblock'],
        ['uitvoering', 'execution'],
        ['slijtlaag', 'wearlayer'],
        ['prioriteit', 'genpriority'],
        ['wegmarkering', 'waymark'],
        ['schade', 'damage'],
        ['toplaag', 'toplayer'],
        ['onderlaag', 'bottomlayer'],
        ['tussenlaag', 'interlayer'],
    ]);

    public importForm: FormGroup;
    public customers: Customer[] = [];
    public projects: CustomerProject[] = [];
    public statuses: Status[] = [];
    public allFeatures: ExcelRow[] = [];
    public excelColumns: string[] = [];
    public featuresProcessed = false;

    /** extra field value mappings: extraKey → (excelValue → allowedOption) */
    public extraFieldMappings: Map<string, Map<string, string>> = new Map();
    /** unique excel values per extra field key */
    public extraFieldUniqueValues: Map<string, string[]> = new Map();

    public saving = false;
    public saveCount = 0;
    public saveTotal = 0;
    public errorMessage: string | null = null;

    /** Project id from the `project` query param, consumed once by the next loadProjects() call. */
    private pendingPrefillProjectId: number | null = null;

    public get extraFieldKeys(): string[] {
        return Object.keys(this.extraFieldOptions);
    }

    public get workareaMappingsFormArray(): FormArray {
        return this.importForm.get('workareaMappings') as FormArray;
    }

    public get extraMappingsFormArray(): FormArray {
        return this.importForm.get('extraMappings') as FormArray;
    }

    public get commentFieldsControl(): FormControl {
        return this.importForm.get('commentFields') as FormControl;
    }

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private fb: FormBuilder,
        private workareaService: WorkareaService,
        private customerService: CustomerService,
        private customerProjectService: CustomerProjectService,
        private dialog: MatDialog,
        private confirmDialogService: ConfirmDialogService,
        private locationService: LocationService,
        private fieldOptionsService: FieldOptionsService
    ) {
        super();
    }

    ngOnInit(): void {
        this.buildForm();

        const queryParams = this.activatedRoute.snapshot.queryParams;
        const prefillCustomerId = queryParams['customer'] ? Number(queryParams['customer']) : null;
        this.pendingPrefillProjectId = queryParams['project'] ? Number(queryParams['project']) : null;

        this.subscriptions.add(
            this.customerService.getList().subscribe(customers => {
                this.customers = customers;
                if (prefillCustomerId && customers.some(c => c.id === prefillCustomerId)) {
                    // Triggers the customer_id valueChanges handler, which loads projects/statuses
                    // and consumes pendingPrefillProjectId.
                    this.importForm.get('customer_id').setValue(prefillCustomerId);
                }
            })
        );
        this.subscriptions.add(
            this.fieldOptionsService.getOptions('general').subscribe(options => {
                this.extraFieldOptions = options as Record<string, string[]>;
                this.extraFieldLabels = Object.keys(this.extraFieldOptions).reduce(
                    (labels, key) => {
                        labels[key] = this.extraFieldLabelTranslations[key] ?? key;
                        return labels;
                    },
                    {} as Record<string, string>
                );
                this.fieldOptionsLoaded = true;
                // Rebuild extraMappings FormArray now that we know the keys
                const extraMappingControls = this.extraFieldKeys.map(() => this.fb.control(''));
                this.importForm.setControl('extraMappings', this.fb.array(extraMappingControls));
            })
        );
    }

    private buildForm(): void {
        const workareaMappingControls = this.workareaFields.map(() => this.fb.control(''));

        this.importForm = this.fb.group({
            customer_id: ['', Validators.required],
            project_id: ['', Validators.required],
            status_id: ['', Validators.required],
            autoFillLocationFields: [false],
            workareaMappings: this.fb.array(workareaMappingControls),
            extraMappings: this.fb.array([]),
            commentFields: [[]],
        });

        this.importForm.get('customer_id').valueChanges.subscribe(customerId => {
            this.importForm.get('project_id').setValue('');
            this.projects = [];
            this.statuses = [];
            this.importForm.get('status_id').setValue('');
            const prefillProjectId = this.pendingPrefillProjectId;
            this.pendingPrefillProjectId = null;
            if (customerId) {
                this.loadProjects(customerId, prefillProjectId);
                this.loadStatuses(customerId);
            }
        });
    }

    private loadProjects(customerId: number, prefillProjectId?: number): void {
        this.subscriptions.add(
            this.customerProjectService.getProjects(false, customerId).subscribe(projects => {
                this.projects = projects ?? [];
                if (prefillProjectId && this.projects.some(p => p.id === prefillProjectId)) {
                    this.importForm.get('project_id').setValue(prefillProjectId);
                }
            })
        );
    }

    private loadStatuses(customerId: number): void {
        const customer = this.customers.find(c => c.id === customerId);
        const customerType = customer?.customer_type ?? 'general';
        this.workareaService.getAvailableStatuses(customerType).then(statuses => {
            this.statuses = statuses;
            const defaultStatus = statuses.find(status => status.isdefault);
            if (defaultStatus) {
                this.importForm.get('status_id').setValue(defaultStatus.id);
            }
        });
    }

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files?.length) {
            return;
        }
        this.readExcelFile(input.files[0]);
        input.value = '';
    }

    private async readExcelFile(file: File): Promise<void> {
        this.allFeatures = [];
        this.excelColumns = [];
        this.extraFieldUniqueValues = new Map();
        this.extraFieldMappings = new Map();
        this.featuresProcessed = false;
        this.commentFieldsControl.setValue([]);
        this.errorMessage = null;

        try {
            const arrayBuffer = await file.arrayBuffer();
            const workbook = read(arrayBuffer, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const rows = utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' });

            this.allFeatures = rows.map(row => ({ properties: row }));

            if (this.allFeatures.length === 0) {
                this.errorMessage = 'Geen rijen gevonden in het Excel-bestand.';
                return;
            }

            const colSet = new Set<string>();
            this.allFeatures.forEach(f => {
                Object.keys(f.properties).forEach(k => colSet.add(k));
            });
            this.excelColumns = Array.from(colSet);
            this.featuresProcessed = true;

            this.applyAutomaticFieldMappings();
        } catch (e) {
            console.error(e);
            this.errorMessage = 'Fout bij het lezen van het Excel-bestand: ' + (e as Error).message;
        }
    }

    private applyAutomaticFieldMappings(): void {
        this.workareaFields.forEach((field, index) => {
            const matchedCol = this.excelColumns.find(col => {
                const lower = col.toLowerCase();
                return this.fieldNameMappings.get(lower) === field || lower === field;
            });
            if (matchedCol) {
                this.workareaMappingsFormArray.at(index).setValue(matchedCol);
            }
        });

        this.extraFieldKeys.forEach((extraKey, index) => {
            const matchedCol = this.excelColumns.find(col => {
                const lower = col.toLowerCase();
                return this.fieldNameMappings.get(lower) === extraKey || lower === extraKey;
            });
            if (matchedCol) {
                this.extraMappingsFormArray.at(index).setValue(matchedCol);
                this.onExtraColumnSelected(extraKey, index, matchedCol);
            }
        });
    }

    public onExtraColumnSelected(extraKey: string, _index: number, column: string): void {
        if (!column) {
            this.extraFieldUniqueValues.delete(extraKey);
            this.extraFieldMappings.delete(extraKey);
            return;
        }

        const uniqueValues = new Set<string>();
        this.allFeatures.forEach(f => {
            const val = f.properties?.[column];
            if (val !== null && val !== undefined && val !== '') {
                uniqueValues.add(String(val));
            }
        });
        this.extraFieldUniqueValues.set(extraKey, Array.from(uniqueValues));

        const allowedOptions = this.extraFieldOptions[extraKey];
        const valueMap = new Map<string, string>();
        uniqueValues.forEach(rawVal => {
            const match = allowedOptions.find(opt => opt.toLowerCase() === rawVal.toLowerCase());
            valueMap.set(rawVal, match ?? '');
        });
        this.extraFieldMappings.set(extraKey, valueMap);
    }

    public setExtraValueMapping(extraKey: string, rawValue: string, mappedValue: string): void {
        if (!this.extraFieldMappings.has(extraKey)) {
            this.extraFieldMappings.set(extraKey, new Map());
        }
        this.extraFieldMappings.get(extraKey).set(rawValue, mappedValue);
    }

    private isValidRDCoordinate(x: number, y: number): boolean {
        return x >= 0 && x <= 290000 && y >= 290000 && y <= 630000;
    }

    private convertCoordinateIfNeeded(lat: number, lng: number): { lat: number; lng: number } {
        if (this.isValidRDCoordinate(lat, lng)) {
            const result = convertToWGS84(lat, lng);
            if (!result.error) {
                return { lat: result.lat, lng: result.lon };
            }
        }
        return { lat, lng };
    }

    private createWorkarea(feature: ExcelRow, customerId: number, projectId: number, statusId: number): Workarea {
        const workarea = new Workarea();
        workarea.status_id = statusId;
        workarea.customer_id = customerId;
        workarea.customer_project_id = projectId;
        workarea.user_id = LocalStorage.getUser()?.id;

        this.workareaFields.forEach((field, index) => {
            const excelCol = this.workareaMappingsFormArray.at(index)?.value;
            if (excelCol && feature.properties?.[excelCol] !== undefined) {
                (workarea as any)[field] = feature.properties[excelCol];
            }
        });

        this.extraFieldKeys.forEach((extraKey, index) => {
            const excelCol = this.extraMappingsFormArray.at(index)?.value;
            if (excelCol && feature.properties?.[excelCol] !== undefined) {
                const rawValue = String(feature.properties[excelCol]);
                const mappedValue = this.extraFieldMappings.get(extraKey)?.get(rawValue) ?? null;
                if (mappedValue) {
                    workarea.extra[extraKey] = mappedValue;
                }
            }
        });

        const rawLat = workarea.lat !== undefined && workarea.lat !== null ? Number(workarea.lat) : null;
        const rawLng = workarea.lng !== undefined && workarea.lng !== null ? Number(workarea.lng) : null;
        if (rawLat !== null && rawLng !== null && !isNaN(rawLat) && !isNaN(rawLng)) {
            const { lat, lng } = this.convertCoordinateIfNeeded(rawLat, rawLng);
            workarea.lat = lat;
            workarea.lng = lng;
        } else {
            workarea.lat = null;
            workarea.lng = null;
        }

        // Append comment fields block
        const selectedCommentFields: string[] = this.commentFieldsControl.value ?? [];
        if (selectedCommentFields.length > 0) {
            const lines = selectedCommentFields
                .filter(
                    col =>
                        feature.properties?.[col] !== undefined &&
                        feature.properties?.[col] !== null &&
                        feature.properties?.[col] !== ''
                )
                .map(col => `${col}: ${feature.properties[col]}`);
            if (lines.length > 0) {
                const block = lines.join('\n');
                workarea.comments = workarea.comments ? `${workarea.comments}\n\n${block}` : block;
            }
        }

        return workarea;
    }

    private fillLocationForWorkarea(workarea: Workarea): Promise<void> {
        return new Promise<void>(resolve => {
            if (!workarea.lat || !workarea.lng) {
                resolve();
                return;
            }
            this.locationService
                .getLocations(workarea.lat, workarea.lng, 'ImportExcelComponent.fillLocationForWorkarea')
                .subscribe({
                    next: (results: any[]) => {
                        if (results?.length) {
                            const components: any[] = results[0].address_components ?? [];
                            const get = (types: string[]) =>
                                components.find((c: any) => types.some(t => c.types.includes(t)))?.long_name ?? '';

                            if (!workarea.street) {
                                workarea.street = get(['route']);
                            }
                            if (!workarea.housenumber) {
                                workarea.housenumber = get(['street_number']);
                            }
                            if (!workarea.zipcode) {
                                workarea.zipcode = get(['postal_code']);
                            }
                            if (!workarea.place) {
                                workarea.place = get(['locality', 'sublocality', 'administrative_area_level_2']);
                            }
                        }
                        resolve();
                    },
                    error: () => resolve(),
                });
        });
    }

    public async importWorkareas(): Promise<void> {
        if (this.importForm.invalid) {
            this.importForm.markAllAsTouched();
            return;
        }
        if (this.allFeatures.length === 0) {
            this.errorMessage = 'Geen rijen om te importeren. Upload eerst een Excel-bestand.';
            return;
        }

        const confirmed = await this.confirmDialogService
            .confirm(
                'Werkvakken importeren',
                `Weet u zeker dat u ${this.allFeatures.length} werkvak(ken) wilt importeren?`,
                'Importeren',
                'Annuleren'
            )
            .then(() => true)
            .catch(() => false);

        if (!confirmed) {
            return;
        }

        const customerId = Number(this.importForm.get('customer_id').value);
        const projectId = Number(this.importForm.get('project_id').value);
        const statusId = Number(this.importForm.get('status_id').value);
        const autoFill = this.importForm.get('autoFillLocationFields').value as boolean;

        const workareas: Workarea[] = this.allFeatures.map(f =>
            this.createWorkarea(f, customerId, projectId, statusId)
        );

        // Workareas without coordinates are rejected, so stop before the batch is half imported.
        const withoutCoordinates = workareas.filter(w => w.lat === null || w.lng === null);
        if (withoutCoordinates.length > 0) {
            this.errorMessage =
                `${withoutCoordinates.length} van de ${workareas.length} werkvak(ken) hebben geen bruikbare coordinaten, ` +
                'daardoor kan er geen locatie bepaald worden. Controleer het Excel-bestand en probeer het opnieuw.';
            return;
        }

        if (autoFill) {
            for (const workarea of workareas) {
                await this.fillLocationForWorkarea(workarea);
            }
        }

        this.saving = true;
        this.saveCount = 0;
        this.saveTotal = workareas.length;
        this.errorMessage = null;

        from(workareas)
            .pipe(concatMap(workarea => from(this.workareaService.saveWorkarea(workarea))))
            .subscribe({
                next: () => {
                    this.saveCount++;
                },
                error: err => {
                    console.error('Fout bij opslaan werkvak', err);
                    this.saving = false;
                    this.errorMessage = 'Er is een fout opgetreden bij het opslaan van een werkvak.';
                },
                complete: () => {
                    this.saving = false;
                    this.workareaService.resetLastCustomerId();
                    this.router.navigate(['/', this.Routenames.customers, customerId], {
                        queryParams: { project: projectId },
                    });
                },
            });
    }

    public openFeaturePreviewDialog(): void {
        this.dialog.open(FeaturePreviewDialogComponent, {
            data: { features: this.allFeatures, columns: this.excelColumns },
            width: '90vw',
            maxWidth: '90vw',
        });
    }
}
